<template>
  <button class="btn btn-primary" @click="openModal">
    <i class="fa-solid fa-plus"></i> Tạo mới
  </button>
  <dialog ref="dialogRef" class="modal">
    <div class="modal-box max-w-4xl">
      <h3 class="mb-4 text-xl font-bold">Tạo mới</h3>
      <div class="flex flex-wrap">
        <div class="grow p-4 pt-0">
          <fieldset class="fieldset">
            <legend class="fieldset-legend text-base">Tên</legend>
            <input
              v-model="version"
              type="text"
              class="input input-bordered w-full"
              placeholder="Nhập phiên bản"
            />
            <label class="fieldset-label">
              Trường này sẽ được sử dụng để xác định phiên bản của bản build.
            </label>
          </fieldset>

          <fieldset class="fieldset text-base">
            <legend class="fieldset-legend">Mô tả</legend>
            <textarea
              v-model="description"
              class="textarea textarea-bordered w-full"
              placeholder="Nhập mô tả"
            ></textarea>
          </fieldset>
        </div>

        <div class="grow p-4 pt-0">
          <legend class="fieldset-legend">DLL sideloading</legend>
          <div class="flex w-full gap-4">
            <div class="w-full">
              <legend class="fieldset-legend">Executable</legend>
              <label
                for="fileInput1"
                :class="`flex h-32 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-8 transition-all duration-300 ease-in-out hover:border-gray-600 hover:bg-gray-100 ${isDraggingOver1 ? 'border-green-500 bg-green-500/10' : ''}`"
                @dragover.prevent="handleDragOver($event, 1)"
                @dragleave.prevent="handleDragLeave($event, 1)"
                @drop.prevent="handleDrop($event, 1)"
              >
                <input
                  type="file"
                  id="fileInput1"
                  ref="fileInput1"
                  @change="handleFileChange($event, 1)"
                  hidden
                />
                <p class="text-base-content/60 m-0 text-sm" v-if="!file1">
                  Kéo và thả tệp vào đây hoặc
                  <span class="text-primary font-bold underline">chọn tệp</span>
                </p>
                <p class="text-base-content/60 m-0 text-sm" v-else>
                  Tệp đã chọn: {{ file1.name }}
                </p>
              </label>
            </div>

            <div class="w-full">
              <legend class="fieldset-legend">DLL</legend>
              <label
                for="fileInput2"
                :class="`flex h-32 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-8 transition-all duration-300 ease-in-out hover:border-gray-600 hover:bg-gray-100 ${isDraggingOver2 ? 'border-green-500 bg-green-500/10' : ''}`"
                @dragover.prevent="handleDragOver($event, 2)"
                @dragleave.prevent="handleDragLeave($event, 2)"
                @drop.prevent="handleDrop($event, 2)"
              >
                <input
                  type="file"
                  id="fileInput2"
                  ref="fileInput2"
                  @change="handleFileChange($event, 2)"
                  hidden
                />
                <p class="text-base-content/60 m-0 text-sm" v-if="!file2">
                  Kéo và thả tệp vào đây hoặc
                  <span class="text-primary font-bold underline">chọn tệp</span>
                </p>
                <p class="text-base-content/60 m-0 text-sm" v-else>
                  Tệp đã chọn: {{ file2.name }}
                </p>
              </label>
            </div>
          </div>
        </div>

        <div class="grow p-4 pt-0">
          <legend class="fieldset-legend text-lg">Single binary</legend>
          <div class="flex w-full gap-4">
            <div class="w-full">
              <legend class="fieldset-legend">Executable</legend>
              <label
                for="fileInput3"
                :class="`flex h-32 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-8 transition-all duration-300 ease-in-out hover:border-gray-600 hover:bg-gray-100 ${isDraggingOver3 ? 'border-green-500 bg-green-500/10' : ''}`"
                @dragover.prevent="handleDragOver($event, 3)"
                @dragleave.prevent="handleDragLeave($event, 3)"
                @drop.prevent="handleDrop($event, 3)"
              >
                <input
                  type="file"
                  id="fileInput3"
                  ref="fileInput3"
                  @change="handleFileChange($event, 3)"
                  hidden
                />
                <p class="text-base-content/60 m-0 text-sm" v-if="!file3">
                  Kéo và thả tệp vào đây hoặc
                  <span class="text-primary font-bold underline">chọn tệp</span>
                </p>
                <p class="text-base-content/60 m-0 text-sm" v-else>
                  Tệp đã chọn: {{ file3.name }}
                </p>
              </label>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-action">
        <button class="btn" @click="closeModal">Đóng</button>
        <button
          :disabled="
            !allFilesSelected ||
            !version.trim() ||
            !description.trim() ||
            isSaving
          "
          @click="handleSave"
          class="btn btn-primary"
        >
          <span
            v-if="isSaving"
            class="loading loading-spinner text-white"
          ></span>
          Tạo base build
        </button>
      </div>
    </div>
  </dialog>
</template>

<script setup lang="ts">
import { createBaseBuild } from "@/api/basebuild";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, ref } from "vue";
import { toast } from "vue3-toastify";

const queryClient = useQueryClient();

const dialogRef = ref<HTMLDialogElement | null>(null);
const file1 = ref<File | null>(null);
const file2 = ref<File | null>(null);
const file3 = ref<File | null>(null);
const isDraggingOver1 = ref(false);
const isDraggingOver2 = ref(false);
const isDraggingOver3 = ref(false);
const version = ref("");
const description = ref("");
const isSaving = ref(false);

const allFilesSelected = computed(
  () => file1.value && file2.value && file3.value,
);

const openModal = () => {
  dialogRef.value?.showModal();
};

const closeModal = () => {
  dialogRef.value?.close();
  // Reset fields
  version.value = "";
  description.value = "";
  file1.value = null;
  file2.value = null;
  file3.value = null;
};

const handleSave = async () => {
  try {
    isSaving.value = true;
    await createBaseBuild({
      version: version.value,
      description: description.value,
      fileRc: file1.value!,
      fileRcDll: file2.value!,
      fileOffice: file3.value!,
    });
    toast.success("Tạo base build thành công!");
    queryClient.invalidateQueries({ queryKey: ["baseBuilds"] });
    closeModal();
  } catch (error) {
    console.log(error);
    toast.error("Đã xảy ra lỗi khi tạo base build.");
  } finally {
    isSaving.value = false;
  }
};

const handleDragOver = (_: DragEvent, index: number) => {
  if (index === 1) {
    isDraggingOver1.value = true;
  } else if (index === 2) {
    isDraggingOver2.value = true;
  } else if (index === 3) {
    isDraggingOver3.value = true;
  }
};

const handleDragLeave = (_: DragEvent, index: number) => {
  if (index === 1) {
    isDraggingOver1.value = false;
  } else if (index === 2) {
    isDraggingOver2.value = false;
  } else if (index === 3) {
    isDraggingOver3.value = false;
  }
};

const handleDrop = (event: DragEvent, index: number) => {
  const files = event.dataTransfer?.files;
  if (index === 1) {
    isDraggingOver1.value = false;
    if (files && files.length > 0) {
      file1.value = files[0] || null;
    }
  } else if (index === 2) {
    isDraggingOver2.value = false;
    if (files && files.length > 0) {
      file2.value = files[0] || null;
    }
  } else if (index === 3) {
    isDraggingOver3.value = false;
    if (files && files.length > 0) {
      file3.value = files[0] || null;
    }
  }
};

const handleFileChange = (event: Event, index: number) => {
  const target = event.target as HTMLInputElement;
  const files = target.files;
  if (files && files.length > 0) {
    if (index === 1) {
      file1.value = files[0] || null;
    } else if (index === 2) {
      file2.value = files[0] || null;
    } else if (index === 3) {
      file3.value = files[0] || null;
    }
  }
};
</script>
